// MIRRORED FILE — the twin lives at the path named in validateDisplayRule.drift.test.ts.
// Both copies must stay logically identical; a drift test enforces it. Edit both, or
// promote the pair to @mld/shared (spec W6) and delete the guard.
import type {ValidatableRule, ValidationError} from './displayRuleTypes';

/** Proportions are stored as NUMERIC(5,2); three 33.33/33.34 bands must pass. */
const PROPORTION_EPSILON = 0.05;

/**
 * Validate that a rule's bands form a total, non-overlapping cover.
 *
 * This is the check that prevents re-shipping the HDL 80-90 gap, where no
 * band owned a value and classification fell through to broken interpolation.
 * Enforced identically at seed time and on every API write.
 */
export function validateDisplayRule(rule: ValidatableRule): ValidationError[] {
    const errors: ValidationError[] = [];

    if (rule.display_mode === 'value_only') {
        if (!rule.reference_text || rule.reference_text.trim().length === 0) {
            errors.push({
                bandIndex: null,
                rule: 'missing_reference_text',
                message: 'A value-only rule must supply reference text.',
            });
        }
        return errors;
    }

    const bands = [...rule.bands].sort((a, b) => a.sort_order - b.sort_order);

    if (bands.length < 2) {
        errors.push({
            bandIndex: null,
            rule: 'too_few_bands',
            message: 'A banded rule needs at least two bands.',
        });
        return errors;
    }

    // Non-finite numbers (e.g. NaN from a mid-edit parseFloat in the admin UI)
    // must be rejected explicitly. Every comparison against NaN is false, so
    // the range/contiguity/sum checks below would otherwise silently pass a
    // band that isn't really a number.
    const nonFiniteBandIndexes = new Set<number>();
    bands.forEach((b, i) => {
        const badFields: string[] = [];
        if (!Number.isFinite(b.proportion)) badFields.push('proportion');
        if (b.min_value !== null && !Number.isFinite(b.min_value)) badFields.push('min_value');
        if (b.max_value !== null && !Number.isFinite(b.max_value)) badFields.push('max_value');
        if (badFields.length > 0) {
            nonFiniteBandIndexes.add(i);
            errors.push({
                bandIndex: i,
                rule: 'non_finite_number',
                message: `Band ${i} has a non-numeric ${badFields.join(' and ')}; enter a valid number before saving.`,
            });
        }
    });

    if (bands[0].min_value !== null) {
        errors.push({
            bandIndex: 0,
            rule: 'first_band_bounded',
            message: 'The first band must be unbounded low (min_value null) so no value falls below every band.',
        });
    }

    const lastIndex = bands.length - 1;
    if (bands[lastIndex].max_value !== null) {
        errors.push({
            bandIndex: lastIndex,
            rule: 'last_band_bounded',
            message: 'The last band must be unbounded high (max_value null) so no value falls above every band.',
        });
    }

    // Each band's own range must not be inverted or empty. Checked
    // independently of the neighbour-boundary check below: an inverted
    // middle band (e.g. min 80, max 40) can make its two neighbours line up
    // exactly at both ends while quietly overlapping each other across the
    // inverted band's range -- the contiguity loop alone cannot see that.
    bands.forEach((b, i) => {
        if (nonFiniteBandIndexes.has(i)) return;
        if (b.min_value !== null && b.max_value !== null && !(b.min_value < b.max_value)) {
            errors.push({
                bandIndex: i,
                rule: 'inverted_band',
                message:
                    `Band ${i} has min_value ${b.min_value} that is not less than max_value ${b.max_value}; ` +
                    `a band's own range must increase from min to max.`,
            });
        }
    });

    // Each band's proportion must describe a real slice of the 0-100 visual
    // width: 0 (or negative) draws nothing, and >100 cannot coexist with any
    // other band while the total still sums to 100. Checked per-band, not
    // just via the sum below: a -50/150 pair sums to exactly 100 and would
    // otherwise sail through the sum check only to fail the DB's
    // ck_bdb_proportion CHECK (proportion > 0 AND proportion <= 100) as a
    // raw constraint violation instead of a clean validation error.
    bands.forEach((b, i) => {
        if (nonFiniteBandIndexes.has(i)) return;
        if (!(b.proportion > 0 && b.proportion <= 100)) {
            errors.push({
                bandIndex: i,
                rule: 'proportion_out_of_range',
                message: `Band ${i} has proportion ${b.proportion}, which is outside the valid (0, 100] range.`,
            });
        }
    });

    for (let i = 1; i < bands.length; i++) {
        const previousMax = bands[i - 1].max_value;
        const currentMin = bands[i].min_value;
        if (previousMax === null || currentMin === null || previousMax !== currentMin) {
            errors.push({
                bandIndex: i,
                rule: 'not_contiguous',
                message:
                    `Band ${i} starts at ${currentMin ?? 'null'} but the previous band ends at ` +
                    `${previousMax ?? 'null'}. Bands must meet exactly — no gap, no overlap.`,
            });
        }
    }

    // Skip the sum check entirely when a proportion was non-finite: the sum
    // itself would be NaN, and reporting both errors would bury the specific,
    // actionable cause (non_finite_number) under a confusing generic one.
    if (nonFiniteBandIndexes.size === 0) {
        const sum = bands.reduce((total, b) => total + b.proportion, 0);
        if (Math.abs(sum - 100) > PROPORTION_EPSILON) {
            errors.push({
                bandIndex: null,
                rule: 'proportion_sum',
                message: `Band proportions sum to ${sum.toFixed(2)}; they must sum to 100.`,
            });
        }
    }

    return errors;
}
